var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@animech/ui";
import * as Styled from "./flow-restriction-findings.style";
import { actions, useAppDispatch } from "store";
import { withNodeLabel, } from "utility/flow-restrictions";
import { AlphabeticalNodeType } from "hooks/graph-panel/graph";
import { useGetResultAlphabeticalId } from "hooks/use-result-alphabetical-id";
/* More letters than this make way for a "+N" */
var maxNodeLabels = 3;
/* The letters of the finding's cups/pumps in the results, or undefined when
 * not all of them have one (e.g. the result is withheld) */
var useNodeLabels = function (_a) {
    var nodeIds = _a.nodeIds, isPump = _a.isPump;
    var getAlphabeticalId = useGetResultAlphabeticalId();
    var labels = nodeIds.map(function (nodeId) {
        return getAlphabeticalId(nodeId, isPump ? AlphabeticalNodeType.PUMP : AlphabeticalNodeType.CUP);
    });
    return labels.every(function (label) { return label !== undefined; })
        ? labels
        : undefined;
};
var NodeLabels = function (_a) {
    var labels = _a.labels;
    var hidden = labels.length > maxNodeLabels;
    var shown = hidden ? labels.slice(0, maxNodeLabels - 1) : labels;
    return (_jsxs(Styled.NodeLabels, { children: [shown.map(function (label) { return (_jsx(Styled.NodeLabel, { children: label }, label)); }), hidden && (_jsxs(Styled.NodeLabel, { title: labels.slice(shown.length).join(", "), children: ["+", labels.length - shown.length] }))] }));
};
var Finding = function (_a) {
    var group = _a.group, onHover = _a.onHover;
    var t = useTranslation().t;
    var _b = __read(useState(false), 2), isExpanded = _b[0], setIsExpanded = _b[1];
    var severity = group.severity, title = group.title, finding = group.finding, recommendations = group.recommendations, nodeIds = group.nodeIds;
    var labels = useNodeLabels(group);
    /* A finding on one cup/pump names it in the text too */
    var withLabel = function (text) {
        return (labels === null || labels === void 0 ? void 0 : labels.length) === 1 ? withNodeLabel(text, labels[0]) : text;
    };
    return (_jsxs(Styled.Finding, { severity: severity, "data-testid": "flow-restriction-finding", onMouseEnter: function () { return onHover(nodeIds); }, children: [_jsxs(Styled.FindingHeader, { onClick: function () { return setIsExpanded(!isExpanded); }, "aria-expanded": isExpanded, children: [_jsx(Styled.SeverityBadge, { severity: severity, title: t("flow_restrictions.severity_".concat(severity)), children: severity === "info" ? "i" : "!" }), _jsx(Styled.FindingTitle, { children: title }), labels ? (_jsx(NodeLabels, { labels: labels })) : (nodeIds.length > 1 && (_jsx(Styled.FindingCount, { children: t("flow_restrictions.count", { count: nodeIds.length }) }))), _jsx(Styled.FindingChevron, { isExpanded: isExpanded, children: _jsx(Icon, { name: "chevron-up", color: "primary" }) })] }), isExpanded && (_jsxs(Styled.FindingBody, { children: [_jsx(Styled.Recommendations, { children: recommendations.map(function (recommendation) { return (_jsx("li", { children: withLabel(recommendation) }, recommendation)); }) }), _jsx("p", { children: withLabel(finding) })] }))] }));
};
/**
 * The calculation API's feedback on how the sense valves/restrictors (and the
 * pumps driving them) behaved in the simulation, with what to change. They
 * start folded to their titles: one unfolded finding already fills most of the
 * room above the dashboard and would hide the others. The text only says "the
 * cup", so hovering a finding highlights its cups/pumps in the scene. The
 * highlight is only let go when leaving the list, not the finding, so moving
 * across the gaps between findings swaps it instead of flashing the scene.
 */
export var FlowRestrictionFindings = function (_a) {
    var groups = _a.groups;
    var dispatch = useAppDispatch();
    var _b = __read(useState([]), 2), hoveredNodeIds = _b[0], setHoveredNodeIds = _b[1];
    useEffect(function () {
        dispatch(actions.setHighlightedNodeIds(hoveredNodeIds));
    }, [dispatch, hoveredNodeIds]);
    /* Folding the box away or a new result unmounts the findings without a
     * mouseleave, so let go of the highlight */
    useEffect(function () { return function () {
        dispatch(actions.setHighlightedNodeIds([]));
    }; }, [dispatch]);
    return (
    /* PlayCanvas listens for the wheel on the window, so without this the
     * wheel zooms the scene instead of scrolling the list */
    _jsx(Styled.Findings, { onWheel: function (event) { return event.stopPropagation(); }, onMouseLeave: function () { return setHoveredNodeIds([]); }, children: groups.map(function (group) { return (_jsx(Finding, { group: group, onHover: setHoveredNodeIds }, [group.isPump, group.severity, group.title, group.finding].join("\n"))); }) }));
};
